
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Plus, Upload, Trash2, Edit2, ArrowUpDown, X, Eye, EyeOff } from 'lucide-react';
import { api } from '../services/api';
import ClientModal from './modals/ClientModal';
import RuleModal from './modals/RuleModal';
import UploadModal from './modals/UploadModal';
import RuleUploadModal from './modals/RuleUploadModal';
import ConfirmDialog from './modals/ConfirmDialog';
import { Client, UnionPaymentTerm } from '../types';
import { readCsvFile } from '../utils/csv';

const ClientManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState<Client[]>([]);
  const [autoRules, setAutoRules] = useState<any[]>([]);
  const [recurringRules, setRecurringRules] = useState<any[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<UnionPaymentTerm[]>([]);
  const [editingPaymentTerm, setEditingPaymentTerm] = useState<UnionPaymentTerm | null>(null);
  const [ptModalOpen, setPtModalOpen] = useState(false);
  const [ptImporting, setPtImporting] = useState(false);
  const [ptImportMsg, setPtImportMsg] = useState('');
  const ptFileRef = useRef<HTMLInputElement>(null);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isRuleUploadModalOpen, setIsRuleUploadModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [confirmDialog, setConfirmDialog] = useState<any>({ isOpen: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [clientFilter, setClientFilter] = useState<'all' | 'satsuma' | 'minamisatsuma' | 'general' | 'densai'>('all');
  const [showLearningOnly, setShowLearningOnly] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' }>({ key: 'client_code', direction: 'asc' });

  // タブ切替時に学習フィルターをリセット
  useEffect(() => { if (activeTab !== 'auto_other') setShowLearningOnly(false); }, [activeTab]);

  const fetchData = async () => {
      setSelectedIds(new Set());
      const clientData = await api.clients.getAll();
      setClients(clientData);
      if (activeTab === 'auto_other') setAutoRules(await api.clients.getAutoOtherRules());
      else if (activeTab === 'recurring') setRecurringRules(await api.clients.getRecurringPayments());
      else if (activeTab === 'payment_terms') setPaymentTerms(await api.paymentTerms.getAll());
  };

  useEffect(() => { fetchData(); }, [activeTab]);

  const filteredData = useMemo(() => {
      let data = activeTab === 'clients' ? clients : activeTab === 'auto_other' ? autoRules : recurringRules;
      if (activeTab === 'clients') {
          if (clientFilter === 'satsuma') {
              // 組合フラグ優先、なければコード判定
              data = data.filter(r => (r.is_union && r.union_type === 'satsuma') || (!r.is_union && r.client_code?.startsWith('88')));
          }
          else if (clientFilter === 'minamisatsuma') {
              data = data.filter(r => (r.is_union && r.union_type === 'minamisatsuma') || (!r.is_union && r.client_code?.startsWith('83')));
          }
          else if (clientFilter === 'general') {
              // 組合でなく、かつコードも該当しないもの
              data = data.filter(r => !r.is_union && !r.client_code?.startsWith('88') && !r.client_code?.startsWith('83'));
          }
          else if (clientFilter === 'densai') {
              data = data.filter(r => !!r.densai_user_code);
          }
      }
      // 自動振分設定タブ：学習機能登録のみ絞り込み
      if (activeTab === 'auto_other' && showLearningOnly) {
          data = data.filter(r => r.notes === '学習機能により自動登録');
      }
      if (searchTerm) {
          const lower = searchTerm.toLowerCase();
          data = data.filter(r => 
              (r.client_code?.toLowerCase().includes(lower)) ||
              (r.client_name?.toLowerCase().includes(lower)) ||
              (r.remitter_name?.toLowerCase().includes(lower)) ||
              (r.client_kana?.toLowerCase().includes(lower)) ||
              (r.client_abbreviation?.toLowerCase().includes(lower)) || 
              (r.imported_name?.toLowerCase().includes(lower))
          );
      }
      return [...data].sort((a, b) => {
          let valA = a[sortConfig.key] || '';
          let valB = b[sortConfig.key] || '';
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
      });
  }, [activeTab, clients, autoRules, recurringRules, searchTerm, clientFilter, showLearningOnly, sortConfig]);

  const clientsMap = useMemo(() => {
    const map = new Map<string, Client>();
    clients.forEach(c => map.set(c.client_code, c));
    return map;
  }, [clients]);

  const handleCreate = () => {
      if (activeTab === 'clients') {
          setEditingClient(null);
          setIsClientModalOpen(true);
      } else if (activeTab === 'payment_terms') {
          setEditingPaymentTerm({ id: '', client_code: '', client_name: '', payment_cycle: 2, payment_date: '', union_type: 'satsuma' });
          setPtModalOpen(true);
      } else {
          setEditingRule(null);
          setIsRuleModalOpen(true);
      }
  };

  const handleOpenUpload = () => {
      if (activeTab === 'clients') setIsUploadModalOpen(true);
      else if (activeTab === 'payment_terms') ptFileRef.current?.click();
      else setIsRuleUploadModalOpen(true);
  };

  const expandCorporateName = (name: string): string =>
    name
      .replace(/㈱|\(株\)|（株）/g, '株式会社')
      .replace(/㈲|\(有\)|（有）/g, '有限会社')
      .replace(/㈾|\(合\)|（合）/g, '合同会社')
      .replace(/\(資\)|（資）/g, '合資会社')
      .replace(/\(名\)|（名）/g, '合名会社');

  const handlePaymentTermCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setPtImporting(true);
      setPtImportMsg('');
      try {
          // Shift-JIS対応で読み込み
          const text = await readCsvFile(file, 'shift-jis');
          const lines = text.split(/\r?\n/).filter(l => l.trim());
          const rows = lines.slice(1).map(line => {
              const cols = line.split(',');
              const rawName = (cols[1] || '').trim().replace(/^"(.*)"$/, '$1');
              return {
                  client_code: (cols[0] || '').trim().replace(/^"(.*)"$/, '$1'),
                  client_name: expandCorporateName(rawName),
                  payment_date: (cols[3] || '').trim().replace(/^"(.*)"$/, '$1'),
              };
          }).filter(r => r.client_code);
          const count = await api.paymentTerms.importCsv(rows);
          setPtImportMsg(`${count}件を登録しました`);
          setPaymentTerms(await api.paymentTerms.getAll());
      } catch (err) {
          setPtImportMsg('取込に失敗しました');
      } finally {
          setPtImporting(false);
          e.target.value = '';
      }
  };

  const handleDelete = (id: string) => {
      const typeLabel = activeTab === 'clients' ? '得意先マスタ' : activeTab === 'payment_terms' ? '支払サイクル設定' : '設定ルール';
      setConfirmDialog({
          isOpen: true,
          title: `${typeLabel}の削除`,
          message: `選択した${typeLabel}を削除します。よろしいですか？`,
          type: 'danger',
          onConfirm: async () => {
              if (activeTab === 'clients') await api.clients.delete(id);
              else if (activeTab === 'auto_other') await api.clients.deleteAutoRule(id);
              else if (activeTab === 'payment_terms') await api.paymentTerms.delete(id);
              else await api.clients.deleteRecurringRule(id);
              await fetchData();
              setConfirmDialog({isOpen: false});
          }
      });
  };

  const handleDeleteSelected = () => {
      if (selectedIds.size === 0) return;
      const typeLabel = activeTab === 'clients' ? '得意先マスタ' : '設定ルール';
      setConfirmDialog({
          isOpen: true, 
          title: `一括削除（${selectedIds.size}件）`, 
          message: `選択された ${selectedIds.size} 件の${typeLabel}を削除します。`, 
          type: 'danger',
          onConfirm: async () => {
              const ids = Array.from(selectedIds) as string[];
              if (activeTab === 'clients') await api.clients.deleteMultipleClients(ids);
              else if (activeTab === 'auto_other') await api.clients.deleteMultipleAutoRules(ids);
              else if (activeTab === 'recurring') await api.clients.deleteMultipleRecurringRules(ids);
              else if (activeTab === 'payment_terms') await api.paymentTerms.deleteMultiple(ids);
              await fetchData();
              setConfirmDialog({isOpen: false});
          }
      });
  };

  const handleEdit = (record: any) => {
      if (activeTab === 'clients') { setEditingClient(record); setIsClientModalOpen(true); }
      else { setEditingRule(record); setIsRuleModalOpen(true); }
  };

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) setSelectedIds(new Set(filteredData.map(r => r.id)));
      else setSelectedIds(new Set());
  };

  const toggleSelectOne = (id: string) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedIds(next);
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortableHeader = ({ label, sortKey, align = 'left', width }: { label: string, sortKey: string, align?: string, width?: string }) => (
    <th 
      className={`p-2.5 ${width ? width : ''} ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} cursor-pointer hover:bg-gray-100 transition-colors group sticky top-0 bg-gray-50 z-10 border-b text-gray-600 font-black`}
      onClick={() => handleSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
        <span className="text-[13px]">{label}</span>
        <ArrowUpDown size={12} className={`text-gray-400 ${sortConfig.key === sortKey ? 'text-blue-500' : 'opacity-0 group-hover:opacity-100'}`} />
      </div>
    </th>
  );

  return (
    <div className="h-full flex flex-col space-y-3">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <h2 className="text-lg font-black text-gray-800 tracking-tight">得意先マスタ管理</h2>
            <div className="flex flex-wrap gap-2">
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input 
                      type="text" 
                      placeholder="名称・略称・カナ・CD検索" 
                      value={searchTerm} 
                      onChange={(e) => setSearchTerm(e.target.value)} 
                      className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-full w-full outline-none text-[13px] bg-white shadow-xs focus:ring-1 focus:ring-blue-500" 
                    />
                </div>
                {selectedIds.size > 0 && (
                    <button 
                        onClick={handleDeleteSelected}
                        className="bg-red-600 text-white px-3 py-1.5 rounded text-[13px] font-black hover:bg-red-700 shadow-sm transition-all active:scale-95 flex items-center gap-1"
                    >
                        <Trash2 size={14}/> 削除（{selectedIds.size}）
                    </button>
                )}
                <button onClick={handleCreate} className="bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 flex items-center gap-1.5 text-[13px] font-black shadow-sm transition-all active:scale-95"><Plus size={16}/> 新規登録</button>
                <button onClick={handleOpenUpload} className="bg-white border border-gray-300 px-4 py-1.5 rounded hover:bg-gray-50 flex items-center gap-1.5 text-[13px] font-bold shadow-xs transition-all active:scale-95"><Upload size={16}/> 一括取込</button>
            </div>
        </div>

        <div className="flex items-center justify-between border-b border-gray-200">
            <div className="flex gap-2">
                {[
                  {id:'clients', l:'得意先一覧'},
                  {id:'auto_other', l:'自動振分設定'},
                  {id:'recurring', l:'毎月振込設定'},
                  {id:'payment_terms', l:'支払サイクル設定'}
                ].map(t => (
                    <button 
                      key={t.id} 
                      onClick={() => setActiveTab(t.id)} 
                      className={`px-4 py-2 text-[13px] font-black border-b-2 transition-all ${activeTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                      {t.l}
                    </button>
                ))}
            </div>
            {activeTab === 'clients' && (
                <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded text-[10px] mb-1">
                    {[
                      {id:'all',l:'全件'},
                      {id:'satsuma',l:'薩摩'},
                      {id:'minamisatsuma',l:'南さつま'},
                      {id:'general',l:'一般'},
                      {id:'densai',l:'でんさい利用'}
                    ].map(f => (
                        <button key={f.id} onClick={() => setClientFilter(f.id as any)} className={`px-2.5 py-1 rounded font-black transition-all ${clientFilter === f.id ? 'bg-white shadow-xs text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                          {f.l}
                        </button>
                    ))}
                </div>
            )}
            {activeTab === 'auto_other' && (
                <button
                    onClick={() => setShowLearningOnly(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border mb-1 ${
                        showLearningOnly
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                    title="手動照合の学習機能で登録されたルールのみ表示"
                >
                    {showLearningOnly ? <EyeOff size={14}/> : <Eye size={14}/>}
                    学習機能登録のみ
                </button>
            )}
        </div>
        
        {/* 支払サイクル設定タブ */}
        {activeTab === 'payment_terms' && (
          <div className="flex-1 bg-white border border-gray-200 rounded shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto scrollbar-thin">
              <table className="w-full text-[13px] text-left whitespace-nowrap border-separate border-spacing-0">
                <thead className="bg-gray-50 text-gray-600 font-black">
                  <tr>
                    <th className="p-2.5 w-10 text-center border-b sticky top-0 bg-gray-50 z-10">
                      <input type="checkbox" className="w-3.5 h-3.5 text-blue-600 rounded"
                        checked={paymentTerms.length > 0 && selectedIds.size === paymentTerms.length}
                        onChange={e => { if (e.target.checked) setSelectedIds(new Set(paymentTerms.map(r => r.id))); else setSelectedIds(new Set()); }} />
                    </th>
                    <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px] w-28">得意先CD</th>
                    <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px] w-56">得意先名</th>
                    <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px] w-36 text-center">支払サイクル</th>
                    <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px] w-24 text-center">支払日</th>
                    <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px] w-20 text-center">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paymentTerms.length === 0 ? (
                    <tr><td colSpan={6} className="p-12 text-center text-gray-300 font-bold italic">登録データがありません</td></tr>
                  ) : paymentTerms.filter(r => !searchTerm || r.client_code.includes(searchTerm) || r.client_name.includes(searchTerm)).sort((a, b) => a.client_code.localeCompare(b.client_code)).map(r => (
                    <tr key={r.id} className={`hover:bg-blue-50/10 transition-colors ${selectedIds.has(r.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="p-2.5 text-center border-b">
                        <input type="checkbox" className="w-3.5 h-3.5 text-blue-600 rounded"
                          checked={selectedIds.has(r.id)} onChange={() => toggleSelectOne(r.id)} />
                      </td>
                      <td className="p-2.5 font-mono text-gray-800 font-bold text-[12px] border-b">{r.client_code}</td>
                      <td className="p-2.5 font-bold text-gray-800 border-b text-[13px]">{r.client_name}</td>
                      <td className="p-2.5 text-center border-b">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-black ${r.payment_cycle === 3 ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-purple-100 text-purple-700 border border-purple-200'}`}>
                          翌{r.payment_cycle === 2 ? '々' : '々々'}月払い
                        </span>
                      </td>
                      <td className="p-2.5 text-center border-b text-[13px] text-gray-700">{r.payment_date ? `${r.payment_date}日` : '-'}</td>
                      <td className="p-2.5 text-center border-b">
                        <div className="flex justify-center gap-1.5">
                          <button onClick={() => { setEditingPaymentTerm(r); setPtModalOpen(true); }} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition" title="編集"><Edit2 size={14}/></button>
                          <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 hover:bg-red-100 rounded transition" title="削除"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 bg-gray-50 border-t text-[11px] font-black text-gray-400 flex justify-between items-center uppercase tracking-widest">
              <span>全 {paymentTerms.filter(r => !searchTerm || r.client_code.includes(searchTerm) || r.client_name.includes(searchTerm)).length} 件</span>
              {ptImportMsg && <span className="text-blue-600">{ptImportMsg}</span>}
              {selectedIds.size > 0 && <span className="text-blue-600">{selectedIds.size} 件選択中</span>}
            </div>
          </div>
        )}

        {/* その他タブ共通テーブル */}
        {activeTab !== 'payment_terms' && (
        <div className="flex-1 bg-white border border-gray-200 rounded shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto scrollbar-thin">
                <table className="w-full text-[13px] text-left whitespace-nowrap border-separate border-spacing-0">
                    <thead className="bg-gray-50 text-gray-600 font-black">
                        <tr>
                            <th className="p-2.5 w-10 text-center border-b sticky top-0 bg-gray-50 z-10">
                                <input 
                                    type="checkbox" 
                                    className="w-3.5 h-3.5 text-blue-600 rounded" 
                                    checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <SortableHeader label="得意先CD" sortKey="client_code" width="w-14" />
                            {activeTab === 'clients' ? (
                                <>
                                    <th className="p-2.5 w-24 text-center border-b sticky top-0 bg-gray-50 z-10 text-[13px]">所属</th>
                                    <SortableHeader label="得意先名" sortKey="client_name" width="w-48" />
                                    <SortableHeader label="カナ名" sortKey="client_kana" width="w-40" />
                                    <SortableHeader label="略称" sortKey="client_abbreviation" width="w-32" />
                                    <SortableHeader label="利用者番号" sortKey="densai_user_code" width="w-32" />
                                    <th className="p-2.5 min-w-[300px] text-left border-b sticky top-0 bg-gray-50 z-10 font-black text-[13px]">備考</th>
                                </>
                            ) : (
                                <>
                                    <SortableHeader label="得意先名（マスタ）" sortKey="client_name" width="w-48" />
                                    <SortableHeader label="振込依頼人名（FB）" sortKey="remitter_name" width="w-48" />
                                    <SortableHeader label="設定金額" sortKey={activeTab === 'auto_other' ? 'amount' : 'expected_amount'} align="right" width="w-24" />
                                    <th className="p-2.5 min-w-[300px] text-left border-b sticky top-0 bg-gray-50 z-10 font-black text-[13px]">備考</th>
                                </>
                            )}
                            <th className="p-2.5 text-center w-20 border-b sticky top-0 bg-gray-50 z-10 font-black text-[13px]">操作</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredData.length === 0 ? (
                            <tr><td colSpan={10} className="p-12 text-center text-gray-300 font-bold italic">該当するデータが見つかりません</td></tr>
                        ) : filteredData.map((r) => {
                            const linkedClient = r.client_code ? clientsMap.get(r.client_code) : null;
                            const displayName = r.client_name || linkedClient?.client_name || '-';

                            return (
                                <tr key={r.id} className={`hover:bg-blue-50/10 transition-colors ${selectedIds.has(r.id) ? 'bg-blue-50/50' : ''}`}>
                                    <td className="p-2.5 text-center border-b">
                                        <input 
                                            type="checkbox" 
                                            className="w-3.5 h-3.5 text-blue-600 rounded" 
                                            checked={selectedIds.has(r.id)}
                                            onChange={() => toggleSelectOne(r.id)}
                                        />
                                    </td>
                                    <td className="p-2.5 font-mono text-gray-800 font-bold text-[12px] border-b">{r.client_code || '-'}</td>
                                    {activeTab === 'clients' ? (
                                        <>
                                            <td className="p-2.5 text-center border-b">
                                                {(r.is_union && r.union_type === 'satsuma') || (!r.is_union && r.client_code?.startsWith('88')) ? (
                                                  <span className="inline-block px-1.5 py-0.5 rounded-[3px] text-[11px] font-black bg-purple-100 text-purple-700 border border-purple-200">薩摩</span>
                                                ) : (r.is_union && r.union_type === 'minamisatsuma') || (!r.is_union && r.client_code?.startsWith('83')) ? (
                                                  <span className="inline-block px-1.5 py-0.5 rounded-[3px] text-[11px] font-black bg-blue-100 text-blue-700 border border-blue-200">南さつま</span>
                                                ) : (
                                                  <span className="inline-block px-1.5 py-0.5 rounded-[3px] text-[11px] font-black bg-gray-100 text-gray-600 border border-gray-200">一般</span>
                                                )}
                                            </td>
                                            <td className="p-2.5 font-bold text-gray-800 border-b truncate max-w-[192px] text-[13px]" title={r.client_name}>
                                                {r.client_name}
                                                {r.imported_name && (
                                                    <span className="block text-[10px] text-gray-400 font-normal">
                                                        照合: {r.imported_name}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-2.5 text-gray-800 text-[11px] font-mono border-b truncate max-w-[160px]">{r.client_kana || '-'}</td>
                                            <td className="p-2.5 text-gray-800 border-b font-medium text-[13px]">{r.client_abbreviation || '-'}</td>
                                            <td className="p-2.5 text-gray-800 border-b font-mono text-[12px]">{r.densai_user_code || '-'}</td>
                                            <td className="p-2.5 text-gray-800 border-b truncate max-w-[400px] font-medium text-[13px]" title={r.notes}>{r.notes || '-'}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="p-2.5 font-bold text-gray-800 border-b truncate max-w-[160px] text-[13px]">{displayName}</td>
                                            <td className="p-2.5 text-gray-800 font-bold border-b truncate max-w-[160px] text-[12px]" title={r.remitter_name}>{r.remitter_name}</td>
                                            <td className="p-2.5 text-right font-black border-b text-gray-800 text-[13px]">
                                                {r.is_alias_only ? (
                                                    <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[10px] font-black">自動学習</span>
                                                ) : (r.amount || r.expected_amount ? `¥${(r.amount || r.expected_amount).toLocaleString()}` : <span className="text-gray-300 text-[10px] font-bold">任意一致</span>)}
                                            </td>
                                            <td className="p-2.5 text-gray-800 border-b truncate max-w-[400px] font-medium text-[13px]" title={r.notes}>{r.notes || '-'}</td>
                                        </>
                                    )}
                                    <td className="p-2.5 text-center border-b">
                                        <div className="flex justify-center gap-1.5">
                                            <button onClick={() => handleEdit(r)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition" title="編集"><Edit2 size={14}/></button>
                                            <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 hover:bg-red-100 rounded transition" title="削除"><Trash2 size={14}/></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 bg-gray-50 border-t text-[11px] font-black text-gray-400 flex justify-between items-center uppercase tracking-widest">
                <span>全 {filteredData.length} 件</span>
                {selectedIds.size > 0 && <span className="text-blue-600">{selectedIds.size} 件選択中</span>}
            </div>
        </div>
        )}

        {/* 支払サイクル設定 編集モーダル */}
        {ptModalOpen && editingPaymentTerm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-[420px] p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-black text-gray-800 text-[15px]">支払サイクル設定</h3>
                <button onClick={() => setPtModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] font-black text-gray-600 mb-1">得意先CD</label>
                  <input type="text" value={editingPaymentTerm.client_code}
                    onChange={e => setEditingPaymentTerm({...editingPaymentTerm, client_code: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-[12px] font-black text-gray-600 mb-1">得意先名</label>
                  <input type="text" value={editingPaymentTerm.client_name}
                    onChange={e => setEditingPaymentTerm({...editingPaymentTerm, client_name: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-[12px] font-black text-gray-600 mb-1">支払サイクル</label>
                  <select value={editingPaymentTerm.payment_cycle}
                    onChange={e => setEditingPaymentTerm({...editingPaymentTerm, payment_cycle: Number(e.target.value)})}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-500">
                    <option value={2}>翌々月払い</option>
                    <option value={3}>翌々々月払い</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-black text-gray-600 mb-1">支払日（日）</label>
                  <input type="text" value={editingPaymentTerm.payment_date} placeholder="例：5、10、25"
                    onChange={e => setEditingPaymentTerm({...editingPaymentTerm, payment_date: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setPtModalOpen(false)} className="px-4 py-1.5 text-[13px] border border-gray-300 rounded hover:bg-gray-50 font-black">キャンセル</button>
                <button onClick={async () => {
                  await api.paymentTerms.save(editingPaymentTerm);
                  setPtModalOpen(false);
                  setPaymentTerms(await api.paymentTerms.getAll());
                }} className="px-4 py-1.5 text-[13px] bg-blue-600 text-white rounded hover:bg-blue-700 font-black">保存</button>
              </div>
            </div>
          </div>
        )}

        <input type="file" accept=".csv" ref={ptFileRef} className="hidden" onChange={handlePaymentTermCsvImport} />

        <UploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} type="client" onSuccess={fetchData} />
        <RuleUploadModal isOpen={isRuleUploadModalOpen} onClose={() => setIsRuleUploadModalOpen(false)} type={activeTab === 'auto_other' ? 'auto_other' : 'recurring'} onSuccess={fetchData} />
        <ClientModal isOpen={isClientModalOpen} onClose={() => setIsClientModalOpen(false)} client={editingClient} onSave={async (c) => { await api.clients.save(c); fetchData(); }} />
        <RuleModal isOpen={isRuleModalOpen} onClose={() => setIsRuleModalOpen(false)} type={activeTab === 'auto_other' ? 'auto_other' : 'recurring'} data={editingRule} onSave={async (r) => { if(activeTab === 'auto_other') await api.clients.saveAutoRule(r); else await api.clients.saveRecurringRule(r); fetchData(); }} />
        <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog({isOpen: false})} />
    </div>
  );
};
export default ClientManagement;
